/**
 * useVqeFilterColumns — column-pick wiring for `VqeFilterChipBuilder` (add
 * mode). Reuses the SELECT list's `useInScopeColumnPicker` for the grouped,
 * in-scope-by-default `CommandPickerPopover` data, then `resolve(key)` maps a
 * picked item back to everything the column-scoped `DraftFilterChip` needs:
 *
 *   - the `TableExplorerColumn` adapter (`toTableExplorerColumn`, E2),
 *   - the owning `(schema, table)` from `column.relation` (for MetaInfo-backed
 *     enum / loose-FK / JSON features),
 *   - the `JsonOperatorContext` (built like `FilterWizard`: `useMetaInfo` +
 *     `usePgServerMajorVersion`),
 *   - the engine-quoted, alias-qualified field text used for the WHERE emit,
 *   - a seeded draft `FilterGroup` (`defaultDraftConditionFields`).
 *
 * Multi-table is handled here (pick → resolve owning table); the chip itself
 * stays single-table, so `FilterWizard.tsx` / `FilterChip.tsx` are untouched.
 */

import { useCallback, useMemo } from 'react';

import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { FilterGroup, TableExplorerColumn } from '../../../core/types/workspaceTypes';
import { useMetaInfo } from '../../../common/hooks/useMetaInfo';
import { usePgServerMajorVersion } from '../../../common/hooks/usePgServerMajorVersion';
import { useSqliteSpatialAvailable } from '../../../common/hooks/useSqliteSpatialAvailable';
import {
  defaultDraftConditionFields,
  type JsonOperatorContext,
} from '../../../common/utils/filterUtils';
import { quoteIdent } from '../../../data-profile/utils/sqlIdentifierQuoting';
import type { VqeEditContext } from '../../edit/editContext';
import {
  columnQualifierSql,
  columnTableLabel,
  type ColumnRefs,
} from '../../edit/edits/selectItemHelpers';
import { sqlEngineToDatabaseEngineId } from '../../edit/livePullSchemaSnapshot';
import { toTableExplorerColumn } from './toTableExplorerColumn';
import { useInScopeColumnPicker, type InScopeColumnPicker } from './useInScopeColumnPicker';
import type { Column } from '../../../../sql-resolver/types/schema';
import { lookupRelationColumns } from '../../../../sql-resolver/service/snapshotRelations';

export interface ResolvedFilterColumn {
  /** Seeded draft (operator + empty value) for the chip's initial state. */
  readonly draft: FilterGroup;
  /**
   * The WHERE field SQL: the source's qualifier as the query spells it (alias,
   * else the delimited table token) + the engine-quoted column — e.g.
   * `b."Status"`, `"order items"."qty"`, `[Order Details].[Qty]`.
   */
  readonly quotedField: string;
  /**
   * A SIBLING column of the same relation, quoted and qualified exactly like
   * `quotedField` (`b."lng"` next to `b."lat"`) — the declared lat/lng pair's
   * longitude reference for the spatial emit. Built from the resolved
   * qualifier, never by splitting `quotedField` on dots.
   */
  readonly quoteSiblingField?: (columnName: string) => string;
  /** Adapter consumed by `DraftFilterChip`. */
  readonly column: TableExplorerColumn;
  /** Owning table's real schema/name (for enum / loose-FK / JSON context). */
  readonly schema?: string;
  readonly table: string;
  readonly jsonContext?: JsonOperatorContext;
}

export interface VqeFilterColumns {
  readonly picker: InScopeColumnPicker;
  readonly engine: DatabaseEngineId;
  readonly connectionId?: string;
  readonly database?: string;
  /** Resolve a picked picker key into chip-ready column context, or null. */
  resolve: (key: string) => ResolvedFilterColumn | null;
  /**
   * Resolve a predicate field text (e.g. `b.[AssignedUser]`, `b."status"`,
   * `` b.`status` ``, or bare `Status`) to chip-ready column context.
   * Used by `EditBuilder` to recover the column type from an existing predicate.
   * Returns null when no in-scope column matches.
   */
  resolveByField: (fieldText: string) => ResolvedFilterColumn | null;
}

/**
 * Split a SQL field reference like `b.[AssignedUser]`, `b."status"`,
 * `` b.`status` ``, `Status`, `[My Table].[My Col]` or `s."t.x"."c"` into its
 * optional prefix (the table / alias part — the second-to-last component of a
 * multi-part reference) and column name, stripping dialect-specific quoting
 * and collapsing doubled delimiters. A dot inside a delimited part is part of
 * the name. Text that is not a plain identifier chain (an expression) comes
 * back whole as the name. Pure function — exported for unit testing.
 */
export function splitFieldRef(fieldText: string): { prefix: string | null; name: string } {
  const parts = splitIdentifierChain(fieldText);
  if (!parts) return { prefix: null, name: fieldText.trim() };
  const name = parts[parts.length - 1]!;
  return { prefix: parts.length > 1 ? parts[parts.length - 2]! : null, name };
}

const CLOSE_DELIMITER: Readonly<Record<string, string>> = { '[': ']', '"': '"', '`': '`' };

function splitIdentifierChain(text: string): string[] | null {
  const parts: string[] = [];
  let i = 0;
  const skipWs = () => {
    while (i < text.length && /\s/.test(text[i]!)) i++;
  };
  for (;;) {
    skipWs();
    const close = CLOSE_DELIMITER[text[i] ?? ''];
    if (close) {
      let value = '';
      let j = i + 1;
      for (; j < text.length; j++) {
        if (text[j] !== close) {
          value += text[j];
          continue;
        }
        if (text[j + 1] === close) {
          value += close;
          j++;
          continue;
        }
        break;
      }
      if (j >= text.length) return null;
      parts.push(value);
      i = j + 1;
    } else {
      const m = /^[^\s."`[\]()]+/.exec(text.slice(i));
      if (!m) return null;
      parts.push(m[0]);
      i += m[0].length;
    }
    skipWs();
    if (i >= text.length) return parts;
    if (text[i] !== '.') return null;
    i++;
  }
}

export function useVqeFilterColumns(editContext: VqeEditContext): VqeFilterColumns {
  const engine = sqlEngineToDatabaseEngineId(editContext.service.engineId);
  const connectionId = editContext.service.connectionId;
  const snapshot = editContext.service.getSchemaSnapshot();
  const database = snapshot?.database;
  const snapshotDefaultSchema = snapshot?.defaultSchemas?.[0];
  // No "Show all tables": a condition on a table the statement doesn't read
  // cannot run (correlated outer tables are in scope already).
  const picker = useInScopeColumnPicker(
    editContext,
    `${editContext.documentId}::${editContext.statementId}::filter`,
    { allowOutOfScope: false },
  );
  const sqlEngine = editContext.service.engineId;
  const refs = useMemo<ColumnRefs>(
    () => picker.refs ?? { engine: sqlEngine, sources: [] },
    [picker.refs, sqlEngine],
  );
  const { data: metaInfo } = useMetaInfo(connectionId ?? null, database ?? null);
  const pgMajorVersion = usePgServerMajorVersion(connectionId, database, engine);
  // Same cached SpatiaLite probe Table Explorer's FilterWizard reads, so a
  // SpatiaLite database offers the same native spatial operator here.
  const sqliteSpatialAvailable = useSqliteSpatialAvailable(engine, connectionId, database);

  /**
   * Shared inner builder: map a `Column` to a full `ResolvedFilterColumn`.
   * Factored out so `resolve(key)` and `resolveByField(fieldText)` share the
   * same mapping body without duplication.
   */
  const buildResolved = useCallback(
    (col: Column): ResolvedFilterColumn => {
      // Projected relations carry their own schema on every engine but SQLite
      // (PostgreSQL included: `mia_s1.orders` → 'mia_s1', matching Table
      // Explorer's MetaInfo key), so this only covers a schemaless relation:
      // '' for SQLite / MySQL (their MetaInfo convention), else the
      // snapshot's first default schema.
      const defaultSchema =
        engine === 'sqlite' || engine === 'mysql'
          ? ''
          : (snapshotDefaultSchema ?? (engine === 'pgsql' ? 'public' : 'dbo'));
      const schema = col.relation.schema ?? defaultSchema;
      const table = col.relation.name;
      // Live MetaInfo loose FKs merged like Table Explorer (real FK wins),
      // matched against the table's own column names.
      const column = toTableExplorerColumn(col, {
        schema,
        table,
        looseRelationships: metaInfo?.looseRelationships,
        columnNames: lookupRelationColumns(snapshot, col.relation)?.map((c) => c.qualified.name),
      });
      // `offerSpatialOperator: false` — the chip has no bbox / map input yet
      // (Table Explorer sets the bbox from its map view), so a spatial draft
      // (native SpatiaLite / geometry column or a declared lat/lng pair) would
      // apply nothing. The SpatiaLite probe and the sibling-column qualifier
      // stay wired for when a bbox input lands.
      const jsonContext: JsonOperatorContext | undefined = table
        ? { engine, schema, table, metaInfo, pgMajorVersion, sqliteSpatialAvailable, offerSpatialOperator: false }
        : undefined;
      // The source's qualifier as the query spells it (alias, else the
      // delimited table token) — shared by `quotedField` and every sibling
      // column reference, so the two never disagree.
      const qualifier = columnQualifierSql(col, refs);
      const quotedField = `${qualifier}.${quoteIdent(engine, col.qualified.name)}`;
      const quoteSiblingField = (columnName: string): string => `${qualifier}.${quoteIdent(engine, columnName)}`;
      const draft: FilterGroup = {
        field: col.qualified.name,
        conditions: [defaultDraftConditionFields(column, jsonContext)],
      };
      return { draft, quotedField, quoteSiblingField, column, schema, table, jsonContext };
    },
    [engine, metaInfo, pgMajorVersion, sqliteSpatialAvailable, refs, snapshot, snapshotDefaultSchema],
  );

  const resolve = useCallback(
    (key: string): ResolvedFilterColumn | null => {
      const col = picker.getColumn(key);
      if (!col) return null;
      return buildResolved(col);
    },
    [picker, buildResolved],
  );

  const resolveByField = useCallback(
    (fieldText: string): ResolvedFilterColumn | null => {
      const { prefix, name } = splitFieldRef(fieldText);
      const nameLower = name.toLowerCase();
      // Iterate the picker's listed columns — the statement's own tables (and a
      // subquery's correlated outer ones). A CTE / derived-table column never
      // resolves to a same-named base table's column.
      const allColumns: Column[] = [];
      for (const group of picker.groups) {
        for (const item of group.items) {
          const col = picker.getColumn(item.key);
          if (col) allColumns.push(col);
        }
      }
      // Prefer exact prefix+name match; fall back to name-only when no prefix.
      let match: Column | null = null;
      for (const col of allColumns) {
        // BUG 1: `foldedName` is engine-folded (UPPERCASE on Oracle, e.g.
        // 'DEPARTMENT_NAME') — NOT already lowercase — so it must itself be
        // lower-cased before comparing against `nameLower` (the on-screen
        // field text). Without this, Oracle field resolution never matched.
        const colNameLower = (col.qualified.foldedName ?? col.qualified.name).toLowerCase();
        if (colNameLower !== nameLower) continue;
        if (prefix !== null) {
          const label = columnTableLabel(col, refs);
          if (label.toLowerCase() !== prefix.toLowerCase()) continue;
        }
        match = col;
        break;
      }
      if (!match) return null;
      return buildResolved(match);
    },
    [picker, buildResolved, refs],
  );

  return { picker, engine, connectionId, database, resolve, resolveByField };
}
